import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutContent } from '@/mocks/about';
import { siteInfo as siteData } from '@/mocks/siteInfo';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9srkqd8o1vn02ekul7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* ignore parse error */
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
          || (parsed?.meta as Record<string, unknown>)?.detail as string
          || (parsed?.message as string)
          || responseText;
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormStatus('error');
          setFormError(serverMsg);
        } else {
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const orgSchema = {
    name: siteData.name,
    url: siteData.url,
    description: siteData.description,
    email: siteData.email,
    telephone: siteData.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '新宿区西新宿2-8-1',
      addressLocality: '东京都',
      addressCountry: 'JP',
    },
    sameAs: [siteData.social.twitter, siteData.social.facebook],
  };

  const homepageSchema = {
    name: '关于我们 - 日本区域指南',
    description: aboutContent.brandIntro,
    url: 'https://japan-zone-guide.readdy.ai/about',
  };

  return (
    <>
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="WebPage" data={homepageSchema} />
      <div className="min-h-screen bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto pt-6 pb-14">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Brand Intro */}
          <section className="mb-14 md:mb-20">
            <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-4">
              关于{aboutContent.brandName}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {aboutContent.brandIntro}
            </p>
          </section>

          {/* Mission & Vision */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-14 md:mb-20">
            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                <i className="ri-focus-3-line text-lg"></i>
              </span>
              <h2 className="font-heading font-semibold text-foreground-950 mb-3">我们的使命</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{aboutContent.mission}</p>
            </div>
            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                <i className="ri-eye-line text-lg"></i>
              </span>
              <h2 className="font-heading font-semibold text-foreground-950 mb-3">我们的愿景</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{aboutContent.vision}</p>
            </div>
          </section>

          {/* Values */}
          <section className="mb-14 md:mb-20">
            <h2 className="font-heading text-lg md:text-2xl font-bold text-foreground-950 mb-6 md:mb-8 text-center">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutContent.values.map((v, i) => (
                <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-5 text-center">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-full bg-accent-100 text-accent-600 mb-3">
                    <i className={`${['ri-shield-check-line', 'ri-scales-3-line', 'ri-lightbulb-flash-line', 'ri-heart-line'][i]} text-lg`}></i>
                  </span>
                  <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-1.5">{v.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="mb-14 md:mb-20">
            <div className="bg-background-100 rounded-lg py-10 px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {aboutContent.stats.map((stat, i) => (
                  <div key={i}>
                    <div className="font-heading text-xl md:text-3xl font-bold text-accent-600 mb-1">{stat.value}</div>
                    <div className="text-xs text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Team */}
          <section className="mb-14 md:mb-20">
            <h2 className="font-heading text-lg md:text-2xl font-bold text-foreground-950 mb-6 md:mb-8 text-center">
              核心团队
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {aboutContent.team.map((member, i) => (
                <div key={i} className="text-center">
                  <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-3 rounded-full overflow-hidden bg-background-200">
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-heading font-semibold text-sm text-foreground-950">{member.name}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{member.role}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-14 md:mb-20">
            <h2 className="font-heading text-lg md:text-2xl font-bold text-foreground-950 mb-6 md:mb-8 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
              <div className="space-y-6">
                {aboutContent.milestones.map((m, i) => (
                  <div key={i} className={`flex flex-col md:flex-row gap-4 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="flex-1 hidden md:block"></div>
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-500 text-background-50 font-bold text-xs md:mx-0 mx-auto">
                      {m.year.slice(2)}
                    </div>
                    <div className="flex-1 bg-background-50 rounded-lg border border-background-200/70 p-5">
                      <span className="text-xs text-accent-600 font-medium">{m.year}</span>
                      <h3 className="font-heading font-semibold text-sm text-foreground-950 mt-1 mb-1.5">{m.title}</h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">{m.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="scroll-mt-20">
            <h2 className="font-heading text-lg md:text-2xl font-bold text-foreground-950 mb-6 md:mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-0.5">地址</h3>
                    <p className="text-sm text-foreground-600">{siteData.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-0.5">邮箱</h3>
                    <a href={`mailto:${siteData.email}`} className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200">
                      {siteData.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-0.5">电话</h3>
                    <p className="text-sm text-foreground-600">{siteData.phone}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6">
                <h3 className="font-heading font-semibold text-foreground-950 mb-4">发送消息</h3>
                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">留言</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200 resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-phone-alt"
                  />
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <div className="text-sm text-green-700 bg-green-50 rounded-md px-3 py-2">
                      消息已发送成功，我们会尽快回复您！
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}